import { connect } from 'react-redux'
import { useLocation } from 'react-router-dom'

import loadable from 'components/utils/lazyImport'
import config from 'constants/config'
import { useTripPermissions } from 'tripPlanner/hooks/useTripPermissions'
import { LE_AGENT_HUB_COMPLETE_ACCOUNT_SETUP } from 'agentHub/routes/AgentHubRoutes'
import { useAppSelector } from 'hooks/reduxHooks'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'
import useMetaSearchMobileV2 from 'components/Pages/HotelSearchPage/useMetaSearchMobileV2'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { CHECKOUT_HEADER_PATHS } from './constants'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'
import { WEDDING_GIFT_REGISTRY_CART_ROUTE } from 'weddings/constants/giftRegistry'

const HeaderMain = loadable(() => import('components/App/Header/HeaderMain/HeaderMain'), <div />)
const HeaderCheckout = loadable(() => import('components/App/Header/HeaderCheckout'), <div />)
const HeaderTripPlannerImmersive = loadable(() => import('tripPlanner/components/ImmersiveHeader'), <div />)
const AgentHubGuestHeader = loadable(() => import('agentHub/components/AgentHubGuestHeader'), <div />)

const routesWithNoHeader = [
  '/:regionCode/gift-card/:code',
  '/:regionCode/order/:orderId/experience-gift-details',
  '/:regionCode/trip-planner/trip',
  '/:regionCode/trip-planner/public-trip/:id',
  '/:regionCode/trip-planner/generate-trip',
  '/:regionCode/search/map',
  '/:regionCode/search/store/map',
  '/:regionCode/search/homes-and-villas/map',
  '/:regionCode/search/experiences/map',
  '/:regionCode/account/my-escapes/experiences/:experienceItemId/voucher',
  '/:regionCode/live',
  '/:regionCode/corporate-events/:eventId',
  '/:regionCode/corporate-events/:eventId/faq',
  '/:regionCode/corporate-events/:eventId/other-activities',
  '/:regionCode/corporate-events/:eventId/flights',
  '/:regionCode/corporate-events/:eventId/accommodation',
  '/:regionCode/corporate-events/:eventId/things-to-know',
  '/:regionCode/corporate-events/:eventId/register',
  `/:regionCode${LE_AGENT_HUB_COMPLETE_ACCOUNT_SETUP}`,
  '/:regionCode/trip-planner/chat-trip-planner/:chatId?',
  '/:regionCode/account/communication-preferences/unsubscribe',
  '/:regionCode/weddings/:slug',
  '/:regionCode/weddings/:slug/edit',
  '/:regionCode/weddings',
]

const weddingGiftRegistryCartHeaderPath = WEDDING_GIFT_REGISTRY_CART_ROUTE

const stickySearchHeaderPaths = [
  '/:regionCode/hotels',
  '/:regionCode/tours',
  '/:regionCode/cruises',
  '/:regionCode/car-hire',
  '/:regionCode/experiences',
  '/:regionCode/flights',
  '/:regionCode/trip-planner',
  '/:regionCode/trip-planner/about',
  '/:regionCode',
]

const desktopAndLargerOnlyHeaderPaths = [
  '/:regionCode/support',
]

const searchNavRedesignHiddenHeaderPaths = [
  '/:regionCode/search',
]

interface Props {
  tripPlannerTripId?: string
}

function HeaderRoutes(props: Props) {
  const { tripPlannerTripId } = props
  const { canEdit } = useTripPermissions(tripPlannerTripId)
  const location = useLocation()
  const pathname = location.pathname

  const agentHubAccountSetupComplete = useAppSelector(state => state.agentHub.agentHubAccount.account?.status === 'onboarded')
  const isMobileOrTablet = useIsMobileOrTablet()
  const isMetaSearchMobileV2 = useMetaSearchMobileV2()

  if (matchLocaleAwarePath(pathname, { path: weddingGiftRegistryCartHeaderPath, exact: true })) {
    return <HeaderMain />
  }

  if (matchLocaleAwarePath(pathname, { path: routesWithNoHeader })) {
    return null
  }

  if (matchLocaleAwarePath(pathname, { path: CHECKOUT_HEADER_PATHS })) {
    return <HeaderCheckout />
  }

  if (config.agentHub.isEnabled && !agentHubAccountSetupComplete) {
    return <AgentHubGuestHeader />
  }

  if (tripPlannerTripId && canEdit) {
    return <HeaderTripPlannerImmersive tripId={tripPlannerTripId} />
  }

  if (matchLocaleAwarePath(pathname, { path: stickySearchHeaderPaths, exact: true })) {
    return <HeaderMain stickySearch />
  }

  if (matchLocaleAwarePath(pathname, { path: desktopAndLargerOnlyHeaderPaths, exact: true })) {
    return <CSSBreakpoint min="desktop">
      <HeaderMain />
    </CSSBreakpoint>
  }

  // The mobile/tablet search redesign renders its own in-page header, so the global one is suppressed here.
  if (isMobileOrTablet && matchLocaleAwarePath(pathname, { path: searchNavRedesignHiddenHeaderPaths, exact: true })) {
    return null
  }

  // Meta search mobile V2 hides the global NavBar (logo, drawer, login, search icon).
  if (isMetaSearchMobileV2) {
    return null
  }

  return <HeaderMain />
}

function mapStateToProps(state: App.State) {
  return {
    tripPlannerTripId: state.tripPlanner.currentTripId,
  }
}

export default connect(mapStateToProps)(HeaderRoutes)
